import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  Link,
} from "react-router";
import { Button } from "~/components/ui/button";
import { useEffect, useRef } from "react";
import { logError, setSentryUserContext } from "~/lib/logger";
import { isAutomatedBrowser } from "~/lib/automation";

import type { Route } from "./+types/root";
import { QueryProvider } from "~/providers";
import { Toaster } from "~/components/ui/sonner";
import "./app.css";
import { Sentry } from "~/config/sentry.client.config";
import logo from "~/assets/images/palate-logo.png";
import { useAuthStore } from "~/store/authentication/useAuthStore";
import AuthService from "~/services/authentication/auth";
import {
  refreshSessionDeduped,
  initCrossTabAuthSync,
  resumeProactiveRefreshFromToken,
} from "~/config/axios.config";
import { LoadingScreen } from "~/components/shared/loading-screen";
import { ImpersonationBanner } from "~/components/shared/impersonation-banner";
import { ImpersonationHandoffNotice } from "~/components/shared/impersonation-handoff-notice";
import {
  startImpersonationFromHandoff,
  takeImpersonationHandoffFromUrl,
  useImpersonationHandoffStore,
} from "~/lib/auth/impersonation-handoff";
import { ServiceUnavailableBanner } from "~/components/shared/service-unavailable-banner";
import { BugReporterMount } from "~/components/shared/bug-reporter-mount";
import { NewVersionPrompt } from "~/components/shared/new-version-prompt";
import { Agentation } from "agentation";
import { BOOT_RECOVERY_SCRIPT } from "./boot-recovery";
import { KeepActiveTabsInView } from "~/hooks/useKeepActiveTabsInView";
import { hasPendingLogout } from "~/lib/auth/pending-logout-marker";
import {
  completePendingLogout,
  initPendingLogout,
} from "~/lib/auth/pending-logout";


/**
 * #719 — the floor beneath every per-route title.
 *
 * Without this the SPA shell ships no `<title>` at all: `ssr: false`, so
 * `build/client/index.html` is prerendered from the root route and carries only
 * what the root contributes. Routes set their own titles, but those only exist
 * AFTER hydration — which leaves the tab untitled for the whole boot window (not
 * a short one on restaurant WiFi), and leaves anything that reads the served
 * HTML without executing it — link previews, bookmarks made before the app
 * boots, crawlers — with no title ever.
 *
 * axe does not catch this: `document-title` is evaluated post-hydration, where
 * all 18 floor routes pass. The gap is real but sits outside what a
 * post-hydration scan can see.
 *
 * Per-route `meta` exports override this, so it is purely a default.
 */
export function meta({}: Route.MetaArgs) {
  return [{ title: "Palate Admin" }];
}

export const links: Route.LinksFunction = () => [
  {
    rel: "preload",
    href: "/fonts/Gilroy-Regular.ttf",
    as: "font",
    type: "font/ttf",
    crossOrigin: "anonymous",
  },
  {
    rel: "preload",
    href: "/fonts/Gilroy-Medium.ttf",
    as: "font",
    type: "font/ttf",
    crossOrigin: "anonymous",
  },
  {
    rel: "preload",
    href: "/fonts/Gilroy-Bold.ttf",
    as: "font",
    type: "font/ttf",
    crossOrigin: "anonymous",
  },
  {
    rel: "preload",
    href: logo,
    as: "image",
  },
];

/**
 * Tells the inline boot-recovery script (app/boot-recovery.ts) that React got
 * far enough to render, so its watchdog stands down and it stops treating this
 * load as a dead boot. Deliberately does NOT clear the retry guard — see
 * BootRecoverySuccess for that, and why the two are separate.
 */
function BootRecoveryAck() {
  useEffect(() => {
    const w = window as unknown as { __palateMounted?: boolean };
    // React got far enough to render — enough to stand the watchdog down and
    // stop the failure overlay. NOT enough to clear the one-shot retry guard:
    // Layout also renders during HydrateFallback, i.e. while the user is still
    // staring at the splash. Clearing here handed every subsequent failure a
    // fresh reload and produced a reload loop on staging.
    w.__palateMounted = true;
  }, []);

  return null;
}

/**
 * Clears the one-shot retry guard, and only from App — which renders once
 * hydration has actually completed and a real route is on screen. That is the
 * earliest point we can honestly call the boot a success.
 */
function BootRecoverySuccess() {
  useEffect(() => {
    const w = window as unknown as { __palateBootOk?: () => void };
    w.__palateBootOk?.();
  }, []);

  return null;
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* Inline, and before <Links />, so it is already listening when the
            stylesheet or entry bundle starts loading. A module fetched over the
            network cannot rescue a boot that is failing because fetches fail. */}
        <script dangerouslySetInnerHTML={{ __html: BOOT_RECOVERY_SCRIPT }} />
        <Meta />
        <Links />
      </head>
      <body suppressHydrationWarning>
        {/* In Layout, not App: Layout wraps the ErrorBoundary too, so a route
            that renders an error still counts as "React got here". Mounting the
            ack inside App meant an unmatched route rendered the root
            ErrorBoundary, App never ran, and the watchdog mistook a rendered
            error page for a dead boot. */}
        <BootRecoveryAck />
        <QueryProvider>{children}</QueryProvider>
        <Toaster />
        {/* Bug-255 — also in Layout, not App: a failed chunk usually lands the
            user in an ErrorBoundary, where App (and its siblings) never render. */}
        <NewVersionPrompt />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function HydrateFallback() {
  return <LoadingScreen />;
}

/** Exported for the #931 boot regression test (lib/auth/931-*.test.tsx). */
export function AuthInitializer() {
  // initAuth must run exactly once per app load. The previous version listed
  // token/refreshToken in the effect deps and called setAuth inside the effect,
  // which retriggered the effect on every refresh — producing a cascade of
  // /me and /refresh calls that consumed each other's rotating refresh tokens
  // and tripped the backend rate limiter.
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const { setAuth, setProfile, setInitializing, isAuthenticated } =
      useAuthStore.getState();

    const initAuth = async () => {
      // Ops Panel impersonation handoff: when an internal user clicks
      // "Enter as <Restaurant>" in the ops panel, we land here with
      // ?impersonation_code=<single-use code> (OPS-3.5) — or, from an Ops
      // Panel deployed before that, the legacy ?impersonation_token=<jwt>.
      // The resulting JWT carries everything the backend's JwtStrategy needs
      // to authenticate the request as the restaurant's Owner — but only if
      // we move it into the auth store BEFORE any /me or refresh call fires.
      //
      // Must run before the normal init flow: a new tab may inherit a
      // stale session from localStorage, so the handoff explicitly clears and
      // overwrites (in this tab's own sessionStorage — GH-923) rather than
      // letting cookie-refresh win the race. The params leave the address
      // bar before anything else happens, so a refresh, the back button or a
      // referer can never replay them. See ~/lib/auth/impersonation-handoff.
      const handoff = takeImpersonationHandoffFromUrl();
      if (handoff) {
        try {
          await startImpersonationFromHandoff(handoff);
        } finally {
          setInitializing(false);
        }
        return;
      }

      // Dev bypass: inject fake auth so all protected routes work without a real backend session
      if (import.meta.env.VITE_DEV_BYPASS_AUTH === "true") {
        setAuth({ token: "dev-bypass-token", refreshToken: "dev-bypass-refresh" });
        try {
          const user = await AuthService.getMe();
          // Note: useAuthStore.setProfile strips `.employee` automatically when
          // VITE_DEV_BYPASS_AUTH is on, so the seeded owner lands in admin mode.
          setProfile(user);
        } catch {
          // Backend not running — fall back to a minimal stub profile
          setProfile({
            id: "dev-user",
            email: "admin@palate.dev",
            firstName: "Dev",
            lastName: "Admin",
            phone: null,
            avatarUrl: null,
            status: "ACTIVE",
            emailVerifiedAt: new Date().toISOString(),
            lastLoginAt: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        }
        setInitializing(false);
        return;
      }

      // If we already have a token, verify it and fetch fresh profile data (e.g. after a page reload)
      if (isAuthenticated()) {
        try {
          const user = await AuthService.getMe();
          setProfile(user);

          // Only refresh immediately if the current access token is close to
          // expiry. Skipping an unnecessary round-trip (and an unwanted
          // refresh-token rotation) on every page reload matters for its own
          // sake — pre-fix this fired on every mount, which rotated tokens
          // during serial Playwright tests, and test #6+ landed with a stale
          // token and 401'd out on /modifier-groups / /categories / /items —
          // but GH-924(a): a token with plenty of life left must still leave
          // this tab with a proactive refresh TIMER armed, not none at all.
          // resumeProactiveRefreshFromToken does exactly that (arming the
          // same "2 minutes before expiry" timer a real refresh would), and
          // reports whether it could (a decodable token outside the 5-minute
          // window) so this branch knows whether an immediate refresh is
          // still needed instead.
          const currentToken = useAuthStore.getState().token;
          const timerArmed = currentToken
            ? resumeProactiveRefreshFromToken(currentToken)
            : false;

          if (!timerArmed) {
            try {
              await refreshSessionDeduped();
            } catch {
              // Refresh failed — current token is still valid from getMe() above,
              // the 401 interceptor will handle it when the token actually expires.
            }
          }
        } catch (error) {
          // Silent failure - will be handled by route guards if token is actually invalid
        } finally {
          setInitializing(false);
        }
        return;
      }

      // #931 — a sign-out the server hasn't confirmed yet. The HttpOnly
      // refresh cookie may still be live, and refreshing from it here is
      // exactly how a failed logout used to sign the user straight back in
      // on reload. Re-attempt the server logout instead (it keeps retrying in
      // the background until confirmed) and stay signed out either way.
      if (hasPendingLogout()) {
        void completePendingLogout();
        setInitializing(false);
        return;
      }

      // No access token in store — try cookie-based refresh (HttpOnly refresh cookie)
      try {
        const result = await refreshSessionDeduped();

        if (result.accessToken) {
          // refreshSessionDeduped already called setAuth + scheduleTokenRefresh.
          // Fetch user profile now that the token is set.
          const user = await AuthService.getMe();
          setProfile(user);
        }
      } catch (error) {
        // Silent failure - user is guest
      } finally {
        setInitializing(false);
      }
    };

    initAuth();
  }, []);

  return null;
}

/**
 * Cross-tab auth sync (backend#1854) — mounted once, alongside
 * AuthInitializer. See initCrossTabAuthSync in ~/config/axios.config for
 * what this actually does; this component is only the mount point.
 */
function CrossTabAuthSync() {
  useEffect(() => {
    const unsubscribe = initCrossTabAuthSync();
    return unsubscribe;
  }, []);

  return null;
}

/**
 * #931 — mount point for the pending-logout retries: re-attempt on
 * `online`, a keepalive attempt on unload, and retiring the marker when a new
 * session signs in. See ~/lib/auth/pending-logout.
 */
function PendingLogoutSync() {
  useEffect(() => initPendingLogout(), []);
  return null;
}

function SentryContextSync() {
  const profile = useAuthStore((state) => state.profile);
  const selectedLocationId = useAuthStore((state) => state.selectedLocationId);

  useEffect(() => {
    setSentryUserContext(profile);
  }, [profile, selectedLocationId]);

  return null;
}

function SentryFallback({ resetError }: { resetError: () => void }) {
  return (
    <main className="flex flex-col items-center justify-center min-h-screen p-4 bg-background text-center">
      <div className="max-w-2xl w-full space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <h1 className="text-4xl font-semibold text-main">
          Something went wrong
        </h1>
        <p className="text-lg text-muted-foreground">
          We have been notified and are looking into it.
        </p>

        <Button asChild variant="default" size="lg" className="mt-8 px-10 h-12">
          <Link
            to="/dashboard"
            replace
            onClick={() => {
              resetError();
            }}
          >
            Back to dashboard
          </Link>
        </Button>
      </div>
    </main>
  );
}

export default function App() {
  // OPS-3.5 — a refused or failed support-link handoff replaces the routes
  // with an explanation instead of falling through to a login redirect.
  const hasHandoffFailure = useImpersonationHandoffStore((s) => s.failure !== null);

  return (
    <>
      <BootRecoverySuccess />
      <AuthInitializer />
      <CrossTabAuthSync />
      <PendingLogoutSync />
      <SentryContextSync />
      {/* A tab strip wider than the screen keeps its active tab in view. */}
      <KeepActiveTabsInView />
      <ImpersonationBanner />
      {/* INV-RC-17 — "Service temporarily unavailable — retrying" while the API
          can't serve (a 503 SESSION_STORE_UNAVAILABLE is not a sign-out). */}
      <ServiceUnavailableBanner />
      {/* Hide the Agentation dev widget when running under automation
          (Playwright sets navigator.webdriver = true). The widget renders
          a fixed-position panel that intercepts clicks on top of the page
          and breaks e2e specs even though it sits at the bottom-right —
          some interactions (drag scopes, focus traps) span the whole page. */}
      {import.meta.env.DEV && !isAutomatedBrowser() && <Agentation />}
      {hasHandoffFailure ? (
        <ImpersonationHandoffNotice />
      ) : (
        <Sentry.ErrorBoundary
          fallback={({ resetError }) => (
            <SentryFallback resetError={resetError} />
          )}
        >
          <Outlet />
        </Sentry.ErrorBoundary>
      )}
      {/* Canonical bug reporter (dev/staging only). Mounted outside the error
          boundary so it stays available to report a crashed route. Self-gates
          visibility via BugReporterMount → import.meta.env / VITE_ENVIRONMENT. */}
      <BugReporterMount />
    </>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  // Log error using logger utility (only sends to Sentry in production)
  useEffect(() => {
    logError("Route Error Boundary", error, {
      category: "route_error",
      tags: {
        error_type: "error_boundary",
      },
    });
  }, [error]);

  const is404 = isRouteErrorResponse(error) && error.status === 404;

  // OPS-3.7 — this boundary renders INSTEAD of App (an unmatched URL, a
  // loader/route error), so App's <ImpersonationBanner /> is not on screen
  // here. Without its own copy, an agent in support mode who hits a 404 sees a
  // page with no sign they are still acting as the restaurant — and no Exit.
  // The banner reads the persisted auth store, which is hydrated even though
  // App's AuthInitializer never ran.
  if (is404) {
    return (
      <>
        <ImpersonationBanner />
        <main className="flex flex-col items-center justify-center min-h-screen p-4 bg-background text-center">
          <div className="max-w-md w-full space-y-8 animate-in fade-in zoom-in duration-500">
            <div className="flex justify-center">
              <img src={logo} alt="Palate logo" className="w-60 h-auto" />
            </div>

            <div className="space-y-4">
              <h1 className="text-8xl font-semibold text-primary/10 select-none">
                404
              </h1>
              <h2 className="text-3xl font-semibold tracking-tight text-main sm:text-4xl">
                Page not found
              </h2>
              <p className="text-base text-muted-foreground">
                Sorry, we couldn't find the page you're looking for. It might have
                been moved or doesn't exist.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button asChild size="lg" className="h-12 px-8">
                <Link to="/">Go back home</Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="h-12 px-8"
                onClick={() => window.history.back()}
              >
                Go back
              </Button>
            </div>
          </div>

          {/* Decorative background element */}
          <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
          </div>
        </main>
      </>
    );
  }

  // Handle other types of errors
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 401 ? "Unauthorized" : "Error";
    details = error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <>
      <ImpersonationBanner />
      <main className="flex flex-col items-center justify-center min-h-screen p-4 bg-background text-center">
        <div className="max-w-2xl w-full space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <h1 className="text-4xl font-semibold text-main">{message}</h1>
          <p className="text-lg text-muted-foreground">{details}</p>

          {stack && (
            <div className="mt-8 text-left">
              <pre className="p-4 bg-muted rounded-xl overflow-x-auto text-sm border border-border/50 shadow-sm font-mono leading-relaxed">
                <code className="text-destructive font-semibold">
                  [Error details]
                </code>
                <div className="mt-2 text-muted-foreground opacity-80">
                  {stack}
                </div>
              </pre>
            </div>
          )}

          <Button asChild variant="default" size="lg" className="mt-8 px-10 h-12">
            <Link to="/dashboard" replace>
              Back to dashboard
            </Link>
          </Button>
        </div>
      </main>
    </>
  );
}
